import React, { useState } from "react";
import {
  Ticket as TicketIcon,
  AlertCircle,
  CheckCircle,
  User as UserIcon,
  Download,
  X,
  ChevronDown,
  Play,
} from "lucide-react";
import { Ticket as TicketType, TicketTimelineEvent } from "@/types";
import { Usuario } from "@/types";
import { canTransferTicket, canCloseTicket, canDeleteTicket } from "@/utils/ticketPermissions";
import { exportTicketToPDF } from "@/utils/pdfExporter";
import { useCallback } from "react";
import { TicketResposta } from "@/types";

interface TicketInfoSidebarProps {
  ticket: TicketType;
  usuario: Usuario | null;
  formatDate: (dateString: string) => string;
  systemRespostas: TicketTimelineEvent[];
  onTransferClick: () => void;
  onCloseClick: () => void;
  onDeleteClick: () => void;
  onTagsChange: (tags: any[]) => void;
  onIniciarAtendimento?: () => void;
}

export default function TicketInfoSidebar({
  ticket,
  usuario,
  formatDate,
  systemRespostas,
  onTransferClick,
  onCloseClick,
  onDeleteClick,
  onTagsChange,
  onIniciarAtendimento,
}: TicketInfoSidebarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Verificar se o operador pode iniciar atendimento
  // Deve ser operador (admin/dev), fazer parte do departamento do ticket e o ticket estar aberto
  const podeIniciarAtendimento = 
    usuario && 
    (usuario.nivel === 'admin' || usuario.nivel === 'dev') &&
    ticket.status_id === 1 && // Status "Aberto"
    ticket.departamento_id &&
    usuario.departamentos?.some((dept) => dept.id === ticket.departamento_id);

  const handleExportPDF = useCallback(() => {
    exportTicketToPDF(ticket, systemRespostas);
  }, [ticket, systemRespostas]);

  const canTransfer = canTransferTicket(ticket, usuario);
  const canClose = canCloseTicket(ticket, usuario);
  const canDelete = canDeleteTicket(ticket, usuario);

  // Verificar se o ticket tem respostas de OUTROS usuários (não do criador)
  // O criador pode excluir mesmo que tenha sua própria resposta inicial
  // O backend vai validar se pode excluir, mas vamos ocultar o botão se já tiver respostas de outros
  // para melhor UX (evitar tentativas desnecessárias)
  const hasResponsesFromOthers =
    ticket?.respostas &&
    ticket.respostas.some(
      (r: any) => 
        r.usuario_id !== ticket.usuario_id && 
        !r.usuario_nome?.toLowerCase().includes("sistema")
    );
  const canDeleteTicketValue = canDelete && !hasResponsesFromOthers;

  return (
    <div className="space-y-4 sm:space-y-5 md:space-y-6">
      {/* Informações do Ticket */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mb-4 flex items-center justify-between gap-2 hover:opacity-80 transition-opacity"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <TicketIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Informações
          </h3>
          <ChevronDown
            className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>

        <div
          className={`space-y-4 overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          {/* Título do Ticket */}
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 break-words">
              #{ticket.id}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 break-words">
              {ticket.assunto || "Assunto não especificado"}
            </p>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              Status
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {ticket.status_nome || "—"}
            </span>
          </div>

          {/* Prioridade */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              Prioridade
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {ticket.prioridade_nome || "—"}
            </span>
          </div>

          {/* Departamento */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <TicketIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              Departamento
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {ticket.departamento_nome || "—"}
            </span>
          </div>

          {/* Usuário */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
              <UserIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              Usuário
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {ticket.usuario_nome || "—"}
            </span>
          </div>

          {/* Datas */}
          <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-800/50">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Criado em:</span>
              <span className="text-gray-900 dark:text-gray-100 font-medium">
                {ticket.created_at ? formatDate(ticket.created_at) : "—"}
              </span>
            </div>
            {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Atualizado em:</span>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  {formatDate(ticket.updated_at)}
                </span>
              </div>
            )}
          </div>

          {/* Botões de Ação */}
          <div className="pt-2 border-t border-gray-100 dark:border-gray-800/50 space-y-2">
            {/* Botão de Iniciar Atendimento */}
            {podeIniciarAtendimento && onIniciarAtendimento && (
              <button
                onClick={onIniciarAtendimento}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-200 font-medium text-sm shadow-sm hover:shadow-md"
                title="Iniciar atendimento deste ticket"
              >
                <Play className="w-4 h-4" />
                Iniciar Atendimento
              </button>
            )}
            
            <button
              onClick={handleExportPDF}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-all duration-200 font-medium text-sm shadow-sm hover:shadow-md"
              title="Exportar conversa como PDF"
            >
              <Download className="w-4 h-4" />
              Exportar PDF
            </button>

            <div className="flex gap-2">
              {canTransfer && (
                <button
                  onClick={onTransferClick}
                  disabled={ticket.status_id === 2 || ticket.status_nome === "Fechado"}
                  className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-all duration-200 font-medium text-xs shadow-sm hover:shadow-md ${
                    ticket.status_id === 2 || ticket.status_nome === "Fechado"
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-orange-600 text-white hover:bg-orange-700"
                  }`}
                  title={
                    ticket.status_id === 2 || ticket.status_nome === "Fechado"
                      ? "Ticket fechado não pode ser transferido"
                      : "Transferir ticket para outro departamento"
                  }
                >
                  <TicketIcon className="w-3 h-3" />
                  Transferir
                </button>
              )}

              {canClose && (
                <button
                  onClick={onCloseClick}
                  disabled={ticket.status_nome === "Fechado"}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-200 font-medium text-xs shadow-sm hover:shadow-md"
                >
                  <CheckCircle className="w-3 h-3" />
                  {ticket.status_nome === "Fechado" ? "Fechado" : "Fechar"}
                </button>
              )}

              {canDeleteTicketValue && (
                <button
                  onClick={onDeleteClick}
                  className="inline-flex items-center justify-center p-2 rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md bg-red-600 text-white hover:bg-red-700"
                  title="Excluir ticket"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

